'use client';

import { createContext, useContext } from 'react';
import type { Locale } from './config';
import enMessages from '../../messages/en.json';
import zhMessages from '../../messages/zh.json';

export type Messages = typeof zhMessages;

const messagesMap: Record<Locale, Messages> = {
  zh: zhMessages,
  en: enMessages as unknown as Messages,
};

export function getMessages(locale: Locale): Messages {
  return messagesMap[locale] ?? messagesMap.zh;
}

function resolve(obj: unknown, path: string): unknown {
  return path.split('.').reduce<unknown>((acc, key) => {
    if (acc && typeof acc === 'object' && key in acc) {
      return (acc as Record<string, unknown>)[key];
    }
    return undefined;
  }, obj);
}

export function translate(
  locale: Locale,
  key: string,
  vars?: Record<string, string | number>
): string {
  const val = resolve(messagesMap[locale] ?? messagesMap.zh, key);
  let str = typeof val === 'string' ? val : key;
  if (vars) {
    for (const [k, v] of Object.entries(vars)) {
      str = str.replace(new RegExp(`\\{${k}\\}`, 'g'), String(v));
    }
  }
  return str;
}

export function translateRaw<T = unknown>(locale: Locale, key: string): T {
  return resolve(messagesMap[locale] ?? messagesMap.zh, key) as T;
}

type I18nContextValue = {
  locale: Locale;
  setLocale: (locale: Locale) => void;
  t: (key: string, vars?: Record<string, string | number>) => string;
  raw: <T = unknown>(key: string) => T;
};

export const I18nContext = createContext<I18nContextValue>({
  locale: 'zh',
  setLocale: () => {},
  t: (key) => key,
  raw: <T = unknown,>(key: string): T => key as T,
});

export function useI18n(): I18nContextValue {
  return useContext(I18nContext);
}

export function useLocale(): Locale {
  return useContext(I18nContext).locale;
}

// Compatible with next-intl style: const t = useTranslations('ns'); t('key') / t.raw('key')
type TranslateFn = {
  (key: string, vars?: Record<string, string | number>): string;
  raw: <T = unknown>(key: string) => T;
};

export function useTranslations(namespace?: string): TranslateFn {
  const ctx = useContext(I18nContext);
  const prefix = namespace ? `${namespace}.` : '';

  const fn = Object.assign(
    (key: string, vars?: Record<string, string | number>) =>
      ctx.t(prefix + key, vars),
    {
      raw: <T = unknown,>(key: string): T => ctx.raw<T>(prefix + key) as T,
    }
  ) as TranslateFn;

  return fn;
}
